import {
    Euler,
    Matrix3,
    Matrix4,
    OrthographicCamera,
    PerspectiveCamera,
    Vector3,
} from "three";
import { hashPlacementToUnit } from "./pathPopulatePlacement";
import { startFrameBudget, yieldIfFrameExhausted } from "./cooperativeScheduler";

export type GridConnectionMode = "pathfinding" | "direct";

/** How placement probability varies across the grid volume. */
export type GridPopulationMode =
    | "uniform"
    | "center"
    | "edges"
    | "bottom"
    | "top";

export type ConnectionLineStyle = "solid" | "dashed" | "dotted";

export type ConnectionStyleMode = "uniform" | "random" | "byRouting";

export type ConnectionArrowMode = "none" | "end" | "both" | "random";

export const GRID_CUBES_ARROW_MARKER_ID = "grid-cubes-arrow-end";

export type GridCameraType = "perspective" | "orthographic";

/**
 * A rasterized SVG mask. `alpha` is row-major coverage (0..255) covering the
 * camera viewport: NDC x in [-1,1] maps across width, NDC y in [-1,1] across
 * height (top row = +y).
 */
export interface GridMaskBitmap {
    width: number;
    height: number;
    alpha: Uint8ClampedArray;
}

export type GridCubesProgressPhase = "populate" | "connect" | "project";

export interface GridCubesProgress {
    phase: GridCubesProgressPhase;
    processed: number;
    total: number;
}

/** A projected, depth-sorted drawable ready to emit as SVG. */
export type GridCubesDrawable =
    | {
          kind: "face";
          /** Polygon corners in viewBox coordinates. */
          points: Array<{ x: number; y: number }>;
          depth: number;
          /** Fill color derived from face orientation + palette. */
          color: string;
      }
    | {
          kind: "segment";
          x1: number;
          y1: number;
          x2: number;
          y2: number;
          depth: number;
          color: string;
          strokeWidth: number;
          strokeDasharray?: string;
          markerStart?: boolean;
          markerEnd?: boolean;
      };

export interface GridCubesComputeInput {
    gridX: number;
    gridY: number;
    gridZ: number;
    cellSize: number;
    /** 0..1 probability that a free cell starts a new object. */
    density: number;
    populationMode: GridPopulationMode;
    /** Max object extent per axis, in cells (>= 1). */
    maxObjectCells: number;
    seed: number;
    connectionsEnabled: boolean;
    /** 0..1 fraction of objectCount used as the number of random pairs. */
    connectionDensity: number;
    connectionMode: GridConnectionMode;
    connectionStyleMode: ConnectionStyleMode;
    connectionLineStyle: ConnectionLineStyle;
    connectionLineStylePathfinding: ConnectionLineStyle;
    connectionLineStyleDirect: ConnectionLineStyle;
    connectionStrandCount: number;
    /** Spacing between parallel strands in viewBox units. */
    connectionStrandSpacing: number;
    connectionCurved: boolean;
    connectionArrowMode: ConnectionArrowMode;
    connectionColor: string;
    connectionColorAlt: string;
    connectionWidth: number;
    connectionWidthAlt: number;
    /** Sub-segments per polyline segment (depth interleaving granularity). */
    segmentSubdivisions: number;
    rotationXDeg: number;
    rotationYDeg: number;
    rotationZDeg: number;
    cameraType: GridCameraType;
    cameraFovDeg: number;
    cameraDistance: number;
    backfaceCulling: boolean;
    /** Dark -> light ramp used to shade faces by orientation. */
    palette: string[];
    /** 0..1 ambient floor so faces away from the light are not fully dark. */
    ambient: number;
    /** When true, only objects fully inside the mask (all box corners) are kept. */
    maskEnabled: boolean;
    /** When true with a mask, connection paths only use in-mask cells/segments. */
    connectionsClipToMask: boolean;
    /** Screen-space masks (union); filled area = allowed. */
    masks: GridMaskBitmap[];
    outputMinX: number;
    outputMinY: number;
    outputWidth: number;
    outputHeight: number;
    signal: AbortSignal;
    onProgress?: (info: GridCubesProgress) => void;
}

/** Padded NDC bounds used to map projected geometry into the viewBox. */
export interface GridNdcBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

export interface GridCubesComputeResult {
    drawables: GridCubesDrawable[];
    objectCount: number;
    faceCount: number;
    connectionCount: number;
    /** NDC fit bounds from the last projection (for mask overlay alignment). */
    ndcBounds: GridNdcBounds | null;
}

interface GridBox {
    x0: number;
    y0: number;
    z0: number;
    x1: number;
    y1: number;
    z1: number;
}

interface GridObject {
    box: GridBox;
    center: Vector3;
    centerCell: { x: number; y: number; z: number };
}

const FACE_DEFS: Array<{
    normal: Vector3;
    corners: (b: { min: Vector3; max: Vector3 }) => Vector3[];
}> = [
    {
        normal: new Vector3(1, 0, 0),
        corners: ({ min, max }) => [
            new Vector3(max.x, min.y, min.z),
            new Vector3(max.x, max.y, min.z),
            new Vector3(max.x, max.y, max.z),
            new Vector3(max.x, min.y, max.z),
        ],
    },
    {
        normal: new Vector3(-1, 0, 0),
        corners: ({ min, max }) => [
            new Vector3(min.x, min.y, max.z),
            new Vector3(min.x, max.y, max.z),
            new Vector3(min.x, max.y, min.z),
            new Vector3(min.x, min.y, min.z),
        ],
    },
    {
        normal: new Vector3(0, 1, 0),
        corners: ({ min, max }) => [
            new Vector3(min.x, max.y, min.z),
            new Vector3(min.x, max.y, max.z),
            new Vector3(max.x, max.y, max.z),
            new Vector3(max.x, max.y, min.z),
        ],
    },
    {
        normal: new Vector3(0, -1, 0),
        corners: ({ min, max }) => [
            new Vector3(min.x, min.y, max.z),
            new Vector3(min.x, min.y, min.z),
            new Vector3(max.x, min.y, min.z),
            new Vector3(max.x, min.y, max.z),
        ],
    },
    {
        normal: new Vector3(0, 0, 1),
        corners: ({ min, max }) => [
            new Vector3(min.x, min.y, max.z),
            new Vector3(max.x, min.y, max.z),
            new Vector3(max.x, max.y, max.z),
            new Vector3(min.x, max.y, max.z),
        ],
    },
    {
        normal: new Vector3(0, 0, -1),
        corners: ({ min, max }) => [
            new Vector3(min.x, max.y, min.z),
            new Vector3(max.x, max.y, min.z),
            new Vector3(max.x, min.y, min.z),
            new Vector3(min.x, min.y, min.z),
        ],
    },
];

function clamp01(v: number): number {
    return Math.max(0, Math.min(1, v));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    let h = hex.trim().replace(/^#/, "");
    if (h.length === 3) {
        h = h
            .split("")
            .map((c) => c + c)
            .join("");
    }
    const int = Number.parseInt(h, 16);
    if (!Number.isFinite(int) || h.length !== 6) {
        return { r: 136, g: 136, b: 136 };
    }
    return {
        r: (int >> 16) & 255,
        g: (int >> 8) & 255,
        b: int & 255,
    };
}

function rgbToHex(r: number, g: number, b: number): string {
    const to = (n: number) =>
        Math.max(0, Math.min(255, Math.round(n)))
            .toString(16)
            .padStart(2, "0");
    return `#${to(r)}${to(g)}${to(b)}`;
}

/** Sample a dark->light palette ramp at t in [0,1] with linear interpolation. */
export function samplePalette(palette: string[], t: number): string {
    if (palette.length === 0) return "#888888";
    if (palette.length === 1) return palette[0]!;
    const clamped = clamp01(t);
    const pos = clamped * (palette.length - 1);
    const i = Math.min(palette.length - 2, Math.floor(pos));
    const frac = pos - i;
    const a = hexToRgb(palette[i]!);
    const b = hexToRgb(palette[i + 1]!);
    return rgbToHex(
        a.r + (b.r - a.r) * frac,
        a.g + (b.g - a.g) * frac,
        a.b + (b.b - a.b) * frac,
    );
}

function throwIfAborted(signal: AbortSignal): void {
    if (signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
    }
}

/**
 * True if NDC point (x,y in [-1,1]) is covered by any mask (union). Points
 * outside the viewport are never covered.
 */
function isCoveredByMasks(
    masks: GridMaskBitmap[],
    ndcX: number,
    ndcY: number,
): boolean {
    if (ndcX < -1 || ndcX > 1 || ndcY < -1 || ndcY > 1) return false;
    const u = (ndcX + 1) / 2;
    const v = (1 - ndcY) / 2;
    for (const mask of masks) {
        if (mask.width <= 0 || mask.height <= 0) continue;
        const col = Math.min(
            mask.width - 1,
            Math.max(0, Math.floor(u * mask.width)),
        );
        const row = Math.min(
            mask.height - 1,
            Math.max(0, Math.floor(v * mask.height)),
        );
        if (mask.alpha[row * mask.width + col]! >= 128) return true;
    }
    return false;
}

function objectWorldMinMax(
    obj: GridObject,
    gridX: number,
    gridY: number,
    gridZ: number,
    cellSize: number,
): { min: Vector3; max: Vector3 } {
    const min = cellWorldCenter(
        obj.box.x0,
        obj.box.y0,
        obj.box.z0,
        gridX,
        gridY,
        gridZ,
        cellSize,
    ).addScalar(-cellSize / 2);
    const max = cellWorldCenter(
        obj.box.x1,
        obj.box.y1,
        obj.box.z1,
        gridX,
        gridY,
        gridZ,
        cellSize,
    ).addScalar(cellSize / 2);
    return { min, max };
}

function boxCorners(min: Vector3, max: Vector3): Vector3[] {
    return [
        new Vector3(min.x, min.y, min.z),
        new Vector3(max.x, min.y, min.z),
        new Vector3(min.x, max.y, min.z),
        new Vector3(max.x, max.y, min.z),
        new Vector3(min.x, min.y, max.z),
        new Vector3(max.x, min.y, max.z),
        new Vector3(min.x, max.y, max.z),
        new Vector3(max.x, max.y, max.z),
    ];
}

function isWorldPointInsideMask(
    world: Vector3,
    rotation: Euler,
    camera: PerspectiveCamera | OrthographicCamera,
    masks: GridMaskBitmap[],
): boolean {
    const clip = world.clone().applyEuler(rotation).project(camera);
    if (!Number.isFinite(clip.x) || !Number.isFinite(clip.y)) return false;
    if (clip.z < -1 || clip.z > 1) return false;
    return isCoveredByMasks(masks, clip.x, clip.y);
}

/** Object kept only if every box corner projects inside the mask. */
function isObjectInsideMasks(
    obj: GridObject,
    gridX: number,
    gridY: number,
    gridZ: number,
    cellSize: number,
    rotation: Euler,
    camera: PerspectiveCamera | OrthographicCamera,
    masks: GridMaskBitmap[],
): boolean {
    const { min, max } = objectWorldMinMax(obj, gridX, gridY, gridZ, cellSize);
    for (const corner of boxCorners(min, max)) {
        const clip = corner.clone().applyEuler(rotation).project(camera);
        if (!Number.isFinite(clip.x) || !Number.isFinite(clip.y)) return false;
        if (clip.z < -1 || clip.z > 1) return false;
        if (!isCoveredByMasks(masks, clip.x, clip.y)) return false;
    }
    return true;
}

function trackNdcBounds(
    bounds: GridNdcBounds,
    ndcX: number,
    ndcY: number,
): void {
    bounds.minX = Math.min(bounds.minX, ndcX);
    bounds.maxX = Math.max(bounds.maxX, ndcX);
    bounds.minY = Math.min(bounds.minY, ndcY);
    bounds.maxY = Math.max(bounds.maxY, ndcY);
}

function padNdcBounds(bounds: GridNdcBounds, pad = 0.1): GridNdcBounds {
    return {
        minX: bounds.minX - pad,
        maxX: bounds.maxX + pad,
        minY: bounds.minY - pad,
        maxY: bounds.maxY + pad,
    };
}

/** Estimate NDC bounds from the grid volume (for mask preview before generate). */
export function estimateProjectionNdcBounds(
    input: Pick<
        GridCubesComputeInput,
        | "gridX"
        | "gridY"
        | "gridZ"
        | "cellSize"
        | "rotationXDeg"
        | "rotationYDeg"
        | "rotationZDeg"
        | "cameraType"
        | "cameraFovDeg"
        | "cameraDistance"
        | "outputWidth"
        | "outputHeight"
    >,
): GridNdcBounds {
    const {
        gridX,
        gridY,
        gridZ,
        cellSize,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraType,
        cameraFovDeg,
        cameraDistance,
        outputWidth,
        outputHeight,
    } = input;
    const aspect = outputWidth / Math.max(1, outputHeight);
    const fov = Math.max(5, Math.min(170, cameraFovDeg));
    const distance = Math.max(0.1, cameraDistance);
    let camera: PerspectiveCamera | OrthographicCamera;
    if (cameraType === "orthographic") {
        const halfHeight = Math.max(
            0.1,
            distance * Math.tan((fov * Math.PI) / 180 / 2),
        );
        const halfWidth = halfHeight * aspect;
        camera = new OrthographicCamera(
            -halfWidth,
            halfWidth,
            halfHeight,
            -halfHeight,
            0.01,
            1000,
        );
    } else {
        camera = new PerspectiveCamera(fov, aspect, 0.01, 1000);
    }
    camera.position.set(0, 0, distance);
    camera.lookAt(0, 0, 0);
    camera.updateMatrixWorld(true);
    camera.updateProjectionMatrix();
    const rotation = new Euler(
        (rotationXDeg * Math.PI) / 180,
        (rotationYDeg * Math.PI) / 180,
        (rotationZDeg * Math.PI) / 180,
        "XYZ",
    );
    const hx = (gridX * cellSize) / 2;
    const hy = (gridY * cellSize) / 2;
    const hz = (gridZ * cellSize) / 2;
    const bounds: GridNdcBounds = {
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity,
    };
    for (const corner of boxCorners(
        new Vector3(-hx, -hy, -hz),
        new Vector3(hx, hy, hz),
    )) {
        const clip = corner.clone().applyEuler(rotation).project(camera);
        if (!Number.isFinite(clip.x) || !Number.isFinite(clip.y)) continue;
        trackNdcBounds(bounds, clip.x, clip.y);
    }
    if (!Number.isFinite(bounds.minX)) {
        return { minX: -1, maxX: 1, minY: -1, maxY: 1 };
    }
    return padNdcBounds(bounds);
}

/** Map NDC coords to viewBox using the same fit as projected drawables. */
export function ndcMaskOverlayRect(
    bounds: GridNdcBounds,
    outputMinX: number,
    outputMinY: number,
    outputWidth: number,
    outputHeight: number,
): { x: number; y: number; width: number; height: number } {
    const spanX = Math.max(1e-6, bounds.maxX - bounds.minX);
    const spanY = Math.max(1e-6, bounds.maxY - bounds.minY);
    const mapX = (ndcX: number) =>
        outputMinX + ((ndcX - bounds.minX) / spanX) * outputWidth;
    const mapY = (ndcY: number) =>
        outputMinY + (1 - (ndcY - bounds.minY) / spanY) * outputHeight;
    const x = mapX(-1);
    const y = mapY(1);
    return {
        x,
        y,
        width: mapX(1) - x,
        height: mapY(-1) - y,
    };
}

function cellWorldCenter(
    ix: number,
    iy: number,
    iz: number,
    gridX: number,
    gridY: number,
    gridZ: number,
    cellSize: number,
): Vector3 {
    return new Vector3(
        (ix + 0.5 - gridX / 2) * cellSize,
        (iy + 0.5 - gridY / 2) * cellSize,
        (iz + 0.5 - gridZ / 2) * cellSize,
    );
}

/** 0..1 weight multiplied into per-cell placement probability. */
export function populationWeight(
    x: number,
    y: number,
    z: number,
    gridX: number,
    gridY: number,
    gridZ: number,
    mode: GridPopulationMode,
): number {
    const nx = (x + 0.5) / Math.max(1, gridX);
    const ny = (y + 0.5) / Math.max(1, gridY);
    const nz = (z + 0.5) / Math.max(1, gridZ);
    switch (mode) {
        case "uniform":
            return 1;
        case "center": {
            const dx = nx - 0.5;
            const dy = ny - 0.5;
            const dz = nz - 0.5;
            const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
            const maxD = Math.sqrt(0.75);
            return clamp01(1 - d / maxD);
        }
        case "edges": {
            const dx = nx - 0.5;
            const dy = ny - 0.5;
            const dz = nz - 0.5;
            const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
            const maxD = Math.sqrt(0.75);
            return clamp01(d / maxD);
        }
        case "bottom":
            return clamp01(ny);
        case "top":
            return clamp01(1 - ny);
    }
}

function populateGrid(
    input: GridCubesComputeInput,
): GridObject[] {
    const {
        gridX,
        gridY,
        gridZ,
        cellSize,
        density,
        maxObjectCells,
        seed,
    } = input;
    const occupied = new Uint8Array(gridX * gridY * gridZ);
    const idx = (x: number, y: number, z: number) =>
        x + y * gridX + z * gridX * gridY;
    const objects: GridObject[] = [];
    const maxExtent = Math.max(1, Math.floor(maxObjectCells));
    let ordinal = 0;

    const regionFree = (b: GridBox): boolean => {
        for (let z = b.z0; z <= b.z1; z++) {
            for (let y = b.y0; y <= b.y1; y++) {
                for (let x = b.x0; x <= b.x1; x++) {
                    if (occupied[idx(x, y, z)]) return false;
                }
            }
        }
        return true;
    };

    const markRegion = (b: GridBox): void => {
        for (let z = b.z0; z <= b.z1; z++) {
            for (let y = b.y0; y <= b.y1; y++) {
                for (let x = b.x0; x <= b.x1; x++) {
                    occupied[idx(x, y, z)] = 1;
                }
            }
        }
    };

    for (let z = 0; z < gridZ; z++) {
        for (let y = 0; y < gridY; y++) {
            for (let x = 0; x < gridX; x++) {
                const cell = idx(x, y, z);
                if (occupied[cell]) continue;
                ordinal += 1;
                const weight = populationWeight(
                    x,
                    y,
                    z,
                    gridX,
                    gridY,
                    gridZ,
                    input.populationMode,
                );
                if (weight <= 0) continue;
                const threshold = Math.min(1, density / weight);
                if (hashPlacementToUnit(seed, cell, 0x1b873593) >= threshold) {
                    continue;
                }
                const extX =
                    1 +
                    Math.floor(
                        hashPlacementToUnit(seed, ordinal, 0x9e3779b1) *
                            maxExtent,
                    );
                const extY =
                    1 +
                    Math.floor(
                        hashPlacementToUnit(seed, ordinal, 0x85ebca6b) *
                            maxExtent,
                    );
                const extZ =
                    1 +
                    Math.floor(
                        hashPlacementToUnit(seed, ordinal, 0xc2b2ae35) *
                            maxExtent,
                    );
                let box: GridBox = {
                    x0: x,
                    y0: y,
                    z0: z,
                    x1: Math.min(gridX - 1, x + extX - 1),
                    y1: Math.min(gridY - 1, y + extY - 1),
                    z1: Math.min(gridZ - 1, z + extZ - 1),
                };
                if (!regionFree(box)) {
                    box = { x0: x, y0: y, z0: z, x1: x, y1: y, z1: z };
                }
                markRegion(box);
                const min = cellWorldCenter(
                    box.x0,
                    box.y0,
                    box.z0,
                    gridX,
                    gridY,
                    gridZ,
                    cellSize,
                ).addScalar(-cellSize / 2);
                const max = cellWorldCenter(
                    box.x1,
                    box.y1,
                    box.z1,
                    gridX,
                    gridY,
                    gridZ,
                    cellSize,
                ).addScalar(cellSize / 2);
                objects.push({
                    box,
                    center: min.clone().add(max).multiplyScalar(0.5),
                    centerCell: {
                        x: Math.floor((box.x0 + box.x1) / 2),
                        y: Math.floor((box.y0 + box.y1) / 2),
                        z: Math.floor((box.z0 + box.z1) / 2),
                    },
                });
            }
        }
    }
    return objects;
}

/** Split a polyline into maximal runs of in-mask points (length >= 2). */
function clipPolylineToMask(
    points: Vector3[],
    rotation: Euler,
    camera: PerspectiveCamera | OrthographicCamera,
    masks: GridMaskBitmap[],
): Vector3[][] {
    if (points.length < 2) return [];
    const runs: Vector3[][] = [];
    let current: Vector3[] = [];
    for (const p of points) {
        if (isWorldPointInsideMask(p, rotation, camera, masks)) {
            current.push(p.clone());
        } else if (current.length >= 2) {
            runs.push(current);
            current = [];
        } else {
            current = [];
        }
    }
    if (current.length >= 2) runs.push(current);
    return runs;
}

/** BFS over free cells (plus the two endpoint objects) with 6-neighbor moves. */
function routeThroughGrid(
    a: GridObject,
    b: GridObject,
    objects: GridObject[],
    input: GridCubesComputeInput,
    rotation: Euler,
    camera: PerspectiveCamera | OrthographicCamera,
    masks: GridMaskBitmap[],
    clipToMask: boolean,
): Vector3[] | null {
    const { gridX, gridY, gridZ, cellSize } = input;
    const idx = (x: number, y: number, z: number) =>
        x + y * gridX + z * gridX * gridY;
    const blocked = new Uint8Array(gridX * gridY * gridZ);
    for (const obj of objects) {
        if (obj === a || obj === b) continue;
        const { box } = obj;
        for (let z = box.z0; z <= box.z1; z++) {
            for (let y = box.y0; y <= box.y1; y++) {
                for (let x = box.x0; x <= box.x1; x++) {
                    blocked[idx(x, y, z)] = 1;
                }
            }
        }
    }
    const start = a.centerCell;
    const goal = b.centerCell;
    const startIdx = idx(start.x, start.y, start.z);
    const goalIdx = idx(goal.x, goal.y, goal.z);
    if (blocked[startIdx] || blocked[goalIdx]) return null;

    const parent = new Int32Array(gridX * gridY * gridZ).fill(-1);
    const visited = new Uint8Array(gridX * gridY * gridZ);
    const queue: number[] = [startIdx];
    visited[startIdx] = 1;
    const neighbors = [
        [1, 0, 0],
        [-1, 0, 0],
        [0, 1, 0],
        [0, -1, 0],
        [0, 0, 1],
        [0, 0, -1],
    ];
    let found = false;
    let head = 0;
    while (head < queue.length) {
        const current = queue[head++]!;
        if (current === goalIdx) {
            found = true;
            break;
        }
        const cz = Math.floor(current / (gridX * gridY));
        const rem = current - cz * gridX * gridY;
        const cy = Math.floor(rem / gridX);
        const cx = rem - cy * gridX;
        for (const [dx, dy, dz] of neighbors) {
            const nx = cx + dx!;
            const ny = cy + dy!;
            const nz = cz + dz!;
            if (nx < 0 || ny < 0 || nz < 0) continue;
            if (nx >= gridX || ny >= gridY || nz >= gridZ) continue;
            const nIdx = idx(nx, ny, nz);
            if (visited[nIdx] || blocked[nIdx]) continue;
            if (clipToMask) {
                const cellCenter = cellWorldCenter(
                    nx,
                    ny,
                    nz,
                    gridX,
                    gridY,
                    gridZ,
                    cellSize,
                );
                if (!isWorldPointInsideMask(cellCenter, rotation, camera, masks)) {
                    continue;
                }
            }
            visited[nIdx] = 1;
            parent[nIdx] = current;
            queue.push(nIdx);
        }
    }
    if (!found) return null;

    const cells: number[] = [];
    let node = goalIdx;
    while (node !== -1) {
        cells.push(node);
        if (node === startIdx) break;
        node = parent[node]!;
    }
    cells.reverse();
    return cells.map((c) => {
        const cz = Math.floor(c / (gridX * gridY));
        const rem = c - cz * gridX * gridY;
        const cy = Math.floor(rem / gridX);
        const cx = rem - cy * gridX;
        return cellWorldCenter(cx, cy, cz, gridX, gridY, gridZ, cellSize);
    });
}

interface PairChoice {
    a: number;
    b: number;
}

function buildPairs(
    objectCount: number,
    connectionDensity: number,
    seed: number,
): PairChoice[] {
    if (objectCount < 2) return [];
    const maxPairs = (objectCount * (objectCount - 1)) / 2;
    const target = Math.min(
        maxPairs,
        Math.round(clamp01(connectionDensity) * objectCount),
    );
    const pairs: PairChoice[] = [];
    const used = new Set<string>();
    let attempts = 0;
    let ordinal = 0;
    while (pairs.length < target && attempts < target * 12 + 24) {
        attempts += 1;
        const a = Math.min(
            objectCount - 1,
            Math.floor(
                hashPlacementToUnit(seed, ordinal++, 0x27d4eb2f) * objectCount,
            ),
        );
        const b = Math.min(
            objectCount - 1,
            Math.floor(
                hashPlacementToUnit(seed, ordinal++, 0x165667b1) * objectCount,
            ),
        );
        if (a === b) continue;
        const key = a < b ? `${a}:${b}` : `${b}:${a}`;
        if (used.has(key)) continue;
        used.add(key);
        pairs.push({ a, b });
    }
    return pairs;
}

function subdividePolyline(points: Vector3[], subdivisions: number): Vector3[] {
    const subs = Math.max(1, Math.floor(subdivisions));
    if (points.length < 2) return points;
    if (subs === 1) return points;
    const out: Vector3[] = [points[0]!.clone()];
    for (let i = 1; i < points.length; i++) {
        const p0 = points[i - 1]!;
        const p1 = points[i]!;
        for (let s = 1; s <= subs; s++) {
            const t = s / subs;
            out.push(p0.clone().lerp(p1, t));
        }
    }
    return out;
}

export function estimateGridCubesObjectCount(
    gridX: number,
    gridY: number,
    gridZ: number,
    density: number,
    maxObjectCells: number,
): number {
    const cells = Math.max(0, gridX) * Math.max(0, gridY) * Math.max(0, gridZ);
    const avgVolume = Math.max(1, (1 + Math.max(1, maxObjectCells)) / 2) ** 3;
    return Math.round((cells * clamp01(density)) / avgVolume);
}

export function lineStyleToDashArray(
    style: ConnectionLineStyle,
    strokeWidth: number,
): string | undefined {
    if (style === "solid") return undefined;
    const w = Math.max(0.1, strokeWidth);
    if (style === "dashed") return `${w * 4} ${w * 2}`;
    return `${w} ${w * 1.5}`;
}

export function connectionMarkerDefsSvg(): string {
    return `<defs>
  <marker id="${GRID_CUBES_ARROW_MARKER_ID}" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="strokeWidth">
    <path d="M0,0 L8,4 L0,8 z" fill="context-stroke" />
  </marker>
</defs>`;
}

interface ConnectionAppearance {
    lineStyle: ConnectionLineStyle;
    color: string;
    strokeWidth: number;
    arrow: "none" | "end" | "both";
}

function resolveConnectionArrow(
    pairIndex: number,
    mode: ConnectionArrowMode,
    seed: number,
): "none" | "end" | "both" {
    if (mode === "none" || mode === "end" || mode === "both") return mode;
    const u = hashPlacementToUnit(seed, pairIndex, 0x1bc4e8f7);
    if (u < 0.15) return "both";
    if (u < 0.4) return "end";
    return "none";
}

function resolveConnectionAppearance(
    pairIndex: number,
    routingMode: GridConnectionMode,
    input: Pick<
        GridCubesComputeInput,
        | "connectionStyleMode"
        | "connectionLineStyle"
        | "connectionLineStylePathfinding"
        | "connectionLineStyleDirect"
        | "connectionColor"
        | "connectionColorAlt"
        | "connectionWidth"
        | "connectionWidthAlt"
        | "connectionArrowMode"
        | "seed"
    >,
): ConnectionAppearance {
    const {
        connectionStyleMode,
        connectionLineStyle,
        connectionLineStylePathfinding,
        connectionLineStyleDirect,
        connectionColor,
        connectionColorAlt,
        connectionWidth,
        connectionWidthAlt,
        connectionArrowMode,
        seed,
    } = input;

    let lineStyle = connectionLineStyle;
    if (connectionStyleMode === "random") {
        const u = hashPlacementToUnit(seed, pairIndex, 0x8f3c2a11);
        lineStyle = u < 0.34 ? "solid" : u < 0.67 ? "dashed" : "dotted";
    } else if (connectionStyleMode === "byRouting") {
        lineStyle =
            routingMode === "pathfinding"
                ? connectionLineStylePathfinding
                : connectionLineStyleDirect;
    }

    let color = connectionColor;
    let strokeWidth = connectionWidth;
    if (connectionStyleMode === "random") {
        if (hashPlacementToUnit(seed, pairIndex, 0x4a9c3b2d) < 0.5) {
            color = connectionColorAlt;
        }
        if (hashPlacementToUnit(seed, pairIndex, 0x7e2f1c90) < 0.35) {
            strokeWidth = connectionWidthAlt;
        }
    }

    return {
        lineStyle,
        color,
        strokeWidth,
        arrow: resolveConnectionArrow(pairIndex, connectionArrowMode, seed),
    };
}

function chaikinOnce(points: Vector3[]): Vector3[] {
    if (points.length < 2) return points.map((p) => p.clone());
    const out: Vector3[] = [points[0]!.clone()];
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i]!;
        const p1 = points[i + 1]!;
        out.push(p0.clone().lerp(p1, 0.25), p0.clone().lerp(p1, 0.75));
    }
    out.push(points[points.length - 1]!.clone());
    return out;
}

function smoothWorldPolyline(points: Vector3[], curved: boolean): Vector3[] {
    if (!curved || points.length < 3) return points.map((p) => p.clone());
    return chaikinOnce(points.map((p) => p.clone()));
}

function strandOffsets(count: number, spacing: number): number[] {
    const n = Math.max(1, Math.min(8, Math.round(count)));
    if (n <= 1) return [0];
    const mid = (n - 1) / 2;
    return Array.from({ length: n }, (_, i) => (i - mid) * spacing);
}

function offsetPolyline2D(
    pts: Array<{ x: number; y: number; depth: number }>,
    offset: number,
): Array<{ x: number; y: number; depth: number }> {
    if (pts.length === 0 || offset === 0) return pts.map((p) => ({ ...p }));
    return pts.map((p, i) => {
        const prev = pts[Math.max(0, i - 1)]!;
        const next = pts[Math.min(pts.length - 1, i + 1)]!;
        const dx = next.x - prev.x;
        const dy = next.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len;
        const ny = dx / len;
        return {
            x: p.x + nx * offset,
            y: p.y + ny * offset,
            depth: p.depth,
        };
    });
}

export function formatGridCubesSegmentSvg(
    d: Extract<GridCubesDrawable, { kind: "segment" }>,
    round: (n: number) => number = (n) => n,
): string {
    const dash = d.strokeDasharray
        ? ` stroke-dasharray="${d.strokeDasharray}"`
        : "";
    const mStart = d.markerStart
        ? ` marker-start="url(#${GRID_CUBES_ARROW_MARKER_ID})"`
        : "";
    const mEnd = d.markerEnd
        ? ` marker-end="url(#${GRID_CUBES_ARROW_MARKER_ID})"`
        : "";
    return `<line x1="${round(d.x1)}" y1="${round(d.y1)}" x2="${round(d.x2)}" y2="${round(d.y2)}" stroke="${d.color}" stroke-width="${d.strokeWidth}" stroke-linecap="round"${dash}${mStart}${mEnd} />`;
}

export function drawablesNeedConnectionMarkers(
    drawables: GridCubesDrawable[],
): boolean {
    return drawables.some(
        (d) =>
            d.kind === "segment" && (d.markerStart || d.markerEnd),
    );
}

export async function computeGridCubesAsync(
    input: GridCubesComputeInput,
): Promise<GridCubesComputeResult> {
    const {
        gridX,
        gridY,
        gridZ,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraType,
        cameraFovDeg,
        cameraDistance,
        backfaceCulling,
        palette,
        ambient,
        maskEnabled,
        masks,
        connectionsClipToMask,
        connectionsEnabled,
        connectionDensity,
        connectionMode,
        connectionStyleMode,
        connectionLineStyle,
        connectionLineStylePathfinding,
        connectionLineStyleDirect,
        connectionStrandCount,
        connectionStrandSpacing,
        connectionCurved,
        segmentSubdivisions,
        seed,
        outputMinX,
        outputMinY,
        outputWidth,
        outputHeight,
        signal,
        onProgress,
    } = input;
    throwIfAborted(signal);

    onProgress?.({ phase: "populate", processed: 0, total: 1 });
    const allObjects = populateGrid(input);
    onProgress?.({ phase: "populate", processed: 1, total: 1 });
    if (allObjects.length === 0) {
        return {
            drawables: [],
            objectCount: 0,
            faceCount: 0,
            connectionCount: 0,
            ndcBounds: null,
        };
    }

    // Camera + rotation setup (mirrors Primitive Points).
    const aspect = outputWidth / Math.max(1, outputHeight);
    const fov = Math.max(5, Math.min(170, cameraFovDeg));
    const distance = Math.max(0.1, cameraDistance);
    let camera: PerspectiveCamera | OrthographicCamera;
    if (cameraType === "orthographic") {
        // Frame the ortho frustum to roughly match the perspective framing at
        // the focal plane, so FOV/distance still feel meaningful.
        const halfHeight = Math.max(
            0.1,
            distance * Math.tan((fov * Math.PI) / 180 / 2),
        );
        const halfWidth = halfHeight * aspect;
        camera = new OrthographicCamera(
            -halfWidth,
            halfWidth,
            halfHeight,
            -halfHeight,
            0.01,
            1000,
        );
    } else {
        camera = new PerspectiveCamera(fov, aspect, 0.01, 1000);
    }
    camera.position.set(0, 0, distance);
    camera.lookAt(0, 0, 0);
    camera.updateMatrixWorld(true);
    camera.updateProjectionMatrix();

    const rotation = new Euler(
        (rotationXDeg * Math.PI) / 180,
        (rotationYDeg * Math.PI) / 180,
        (rotationZDeg * Math.PI) / 180,
        "XYZ",
    );
    const rotationMatrix = new Matrix4().makeRotationFromEuler(rotation);
    const normalMatrix = new Matrix3().setFromMatrix4(rotationMatrix);
    const cameraPos = camera.position.clone();
    // For an orthographic camera every ray is parallel to the view axis, so the
    // "toward camera" direction is constant (camera sits on +z looking at origin).
    const orthoViewDir = new Vector3(0, 0, 1);
    const lightDir = new Vector3(0.32, 0.86, 0.4).normalize();
    const ambientFloor = clamp01(ambient);

    // Screen-space mask: keep only objects whose entire box projects inside the mask.
    const useMask = maskEnabled && masks.length > 0;
    const objects = useMask
        ? allObjects.filter((obj) =>
              isObjectInsideMasks(
                  obj,
                  gridX,
                  gridY,
                  gridZ,
                  input.cellSize,
                  rotation,
                  camera,
                  masks,
              ),
          )
        : allObjects;
    if (objects.length === 0) {
        return {
            drawables: [],
            objectCount: 0,
            faceCount: 0,
            connectionCount: 0,
            ndcBounds: null,
        };
    }

    interface ConnectionRoute {
        points: Vector3[];
        appearance: ConnectionAppearance;
    }
    const connectionRoutes: ConnectionRoute[] = [];
    const clipConnections = connectionsClipToMask && useMask;
    let logicalConnectionCount = 0;
    if (connectionsEnabled && objects.length >= 2) {
        const pairs = buildPairs(objects.length, connectionDensity, seed);
        logicalConnectionCount = pairs.length;
        const budget = startFrameBudget();
        for (let i = 0; i < pairs.length; i++) {
            await yieldIfFrameExhausted(budget, signal);
            const pair = pairs[i]!;
            const a = objects[pair.a]!;
            const b = objects[pair.b]!;
            const routingMode: GridConnectionMode =
                connectionMode === "pathfinding" ? "pathfinding" : "direct";
            const appearance = resolveConnectionAppearance(i, routingMode, input);
            let polyline: Vector3[] | null = null;
            if (connectionMode === "pathfinding") {
                polyline = routeThroughGrid(
                    a,
                    b,
                    objects,
                    input,
                    rotation,
                    camera,
                    masks,
                    clipConnections,
                );
            }
            if (!polyline) {
                polyline = [a.center.clone(), b.center.clone()];
            }
            polyline = smoothWorldPolyline(polyline, input.connectionCurved);
            const pushRoute = (pts: Vector3[]) => {
                connectionRoutes.push({
                    points: subdividePolyline(pts, segmentSubdivisions),
                    appearance,
                });
            };
            if (clipConnections) {
                const runs = clipPolylineToMask(
                    polyline,
                    rotation,
                    camera,
                    masks,
                );
                for (const run of runs) pushRoute(run);
            } else {
                pushRoute(polyline);
            }
            onProgress?.({
                phase: "connect",
                processed: i + 1,
                total: pairs.length,
            });
        }
    }

    interface RawFace {
        ndc: Array<{ x: number; y: number }>;
        depth: number;
        color: string;
    }
    interface ProjectedRoute {
        pts: Array<{ x: number; y: number; depth: number }>;
        appearance: ConnectionAppearance;
    }
    const rawFaces: RawFace[] = [];
    const projectedRoutes: ProjectedRoute[] = [];

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const track = (x: number, y: number) => {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
    };

    const projectPoint = (
        world: Vector3,
    ): { x: number; y: number; depth: number } | null => {
        const clip = world.clone().project(camera);
        if (!Number.isFinite(clip.x) || !Number.isFinite(clip.y)) return null;
        if (clip.z < -1 || clip.z > 1) return null;
        return { x: clip.x, y: clip.y, depth: clip.z };
    };

    const totalFaces = objects.length * FACE_DEFS.length;
    onProgress?.({ phase: "project", processed: 0, total: totalFaces });
    const projectBudget = startFrameBudget();
    let processedFaces = 0;
    for (const obj of objects) {
        const min = cellWorldCenter(
            obj.box.x0,
            obj.box.y0,
            obj.box.z0,
            gridX,
            gridY,
            gridZ,
            input.cellSize,
        ).addScalar(-input.cellSize / 2);
        const max = cellWorldCenter(
            obj.box.x1,
            obj.box.y1,
            obj.box.z1,
            gridX,
            gridY,
            gridZ,
            input.cellSize,
        ).addScalar(input.cellSize / 2);
        for (const face of FACE_DEFS) {
            await yieldIfFrameExhausted(projectBudget, signal);
            processedFaces += 1;
            const cornersWorld = face
                .corners({ min, max })
                .map((c) => c.applyEuler(rotation));
            const worldNormal = face.normal
                .clone()
                .applyMatrix3(normalMatrix)
                .normalize();
            const faceCenter = cornersWorld
                .reduce((acc, c) => acc.add(c), new Vector3())
                .multiplyScalar(1 / cornersWorld.length);
            const toCamera =
                cameraType === "orthographic"
                    ? orthoViewDir
                    : cameraPos.clone().sub(faceCenter).normalize();
            const isBackface = worldNormal.dot(toCamera) <= 0;
            if (backfaceCulling && isBackface) continue;

            const projectedCorners: Array<{ x: number; y: number }> = [];
            let depthSum = 0;
            let valid = true;
            for (const c of cornersWorld) {
                const p = projectPoint(c);
                if (!p) {
                    valid = false;
                    break;
                }
                projectedCorners.push({ x: p.x, y: p.y });
                depthSum += p.depth;
            }
            if (!valid) continue;
            const ndotl = clamp01(worldNormal.dot(lightDir));
            const tone = ambientFloor + (1 - ambientFloor) * ndotl;
            for (const pc of projectedCorners) track(pc.x, pc.y);
            rawFaces.push({
                ndc: projectedCorners,
                depth: depthSum / projectedCorners.length,
                color: samplePalette(palette, tone),
            });
            onProgress?.({
                phase: "project",
                processed: processedFaces,
                total: totalFaces,
            });
        }
    }

    for (const route of connectionRoutes) {
        await yieldIfFrameExhausted(projectBudget, signal);
        const projected = route.points
            .map((p) => projectPoint(p.clone().applyEuler(rotation)))
            .filter((p): p is { x: number; y: number; depth: number } => !!p);
        if (projected.length < 2) continue;
        for (const p of projected) track(p.x, p.y);
        projectedRoutes.push({ pts: projected, appearance: route.appearance });
    }

    if (rawFaces.length === 0 && projectedRoutes.length === 0) {
        return {
            drawables: [],
            objectCount: objects.length,
            faceCount: 0,
            connectionCount: logicalConnectionCount,
            ndcBounds: null,
        };
    }

    const pad = 0.1;
    const ndcBounds: GridNdcBounds = {
        minX: minX - pad,
        maxX: maxX + pad,
        minY: minY - pad,
        maxY: maxY + pad,
    };
    const spanX = Math.max(1e-6, ndcBounds.maxX - ndcBounds.minX);
    const spanY = Math.max(1e-6, ndcBounds.maxY - ndcBounds.minY);
    const mapX = (ndcX: number) =>
        outputMinX + ((ndcX - ndcBounds.minX) / spanX) * outputWidth;
    const mapY = (ndcY: number) =>
        outputMinY + (1 - (ndcY - ndcBounds.minY) / spanY) * outputHeight;

    const strandCount = Math.max(
        1,
        Math.min(8, Math.round(input.connectionStrandCount)),
    );
    const strandSpacing = Math.max(0, input.connectionStrandSpacing);

    const drawables: GridCubesDrawable[] = [];
    for (const f of rawFaces) {
        drawables.push({
            kind: "face",
            points: f.ndc.map((p) => ({ x: mapX(p.x), y: mapY(p.y) })),
            depth: f.depth,
            color: f.color,
        });
    }
    for (const route of projectedRoutes) {
        const mapped = route.pts.map((p) => ({
            x: mapX(p.x),
            y: mapY(p.y),
            depth: p.depth,
        }));
        const dash = lineStyleToDashArray(
            route.appearance.lineStyle,
            route.appearance.strokeWidth,
        );
        for (const offset of strandOffsets(strandCount, strandSpacing)) {
            const strandPts =
                offset === 0 ? mapped : offsetPolyline2D(mapped, offset);
            for (let i = 1; i < strandPts.length; i++) {
                const p0 = strandPts[i - 1]!;
                const p1 = strandPts[i]!;
                const isFirst = i === 1;
                const isLast = i === strandPts.length - 1;
                const arrow = route.appearance.arrow;
                drawables.push({
                    kind: "segment",
                    x1: p0.x,
                    y1: p0.y,
                    x2: p1.x,
                    y2: p1.y,
                    depth: (p0.depth + p1.depth) / 2,
                    color: route.appearance.color,
                    strokeWidth: route.appearance.strokeWidth,
                    strokeDasharray: dash,
                    markerStart: arrow === "both" && isFirst,
                    markerEnd:
                        (arrow === "end" || arrow === "both") && isLast,
                });
            }
        }
    }
    drawables.sort((a, b) => b.depth - a.depth);

    return {
        drawables,
        objectCount: objects.length,
        faceCount: rawFaces.length,
        connectionCount: logicalConnectionCount,
        ndcBounds,
    };
}
